<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>POI一括インポート</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="modal-body">
        <div class="info-box">
          <p><strong>対応形式:</strong> CSV, JSON</p>
          <p><strong>最大ファイルサイズ:</strong> 10MB</p>
          <p><strong>必須フィールド:</strong> name, latitude, longitude</p>
        </div>

        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="format">ファイル形式 <span class="required">*</span></label>
            <select id="format" v-model="form.format" required>
              <option value="">選択してください</option>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>

          <div class="form-group">
            <label for="file">ファイル <span class="required">*</span></label>
            <input
              id="file"
              type="file"
              accept=".csv,.json"
              required
              @change="handleFileChange"
            />
            <small v-if="form.file">選択済み: {{ form.file.name }} ({{ formatFileSize(form.file.size) }})</small>
          </div>

          <div class="sample-format">
            <h4>サンプルフォーマット</h4>
            <div v-if="form.format === 'csv'">
              <pre>name,latitude,longitude,category,address,description
東京タワー,35.6586,139.7454,観光スポット,東京都港区芝公園4-2-8,東京のシンボル
スカイツリー,35.7101,139.8107,観光スポット,東京都墨田区押上1-1-2,世界一高いタワー</pre>
            </div>
            <div v-if="form.format === 'json'">
              <pre>[
  {
    "name": "東京タワー",
    "latitude": 35.6586,
    "longitude": 139.7454,
    "category": "観光スポット",
    "address": "東京都港区芝公園4-2-8",
    "description": "東京のシンボル"
  },
  {
    "name": "スカイツリー",
    "latitude": 35.7101,
    "longitude": 139.8107,
    "category": "観光スポット",
    "address": "東京都墨田区押上1-1-2",
    "description": "世界一高いタワー"
  }
]</pre>
            </div>
          </div>

          <div v-if="uploading" class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
            <span class="progress-text">{{ uploadProgress }}%</span>
          </div>

          <div v-if="result" class="success-message">
            <p><strong>インポート成功！</strong></p>
            <p>{{ result.imported_count }} / {{ result.total_count }} 件のPOIをインポートしました</p>
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>

          <div class="modal-footer">
            <button type="button" @click="$emit('close')" class="btn-secondary">
              {{ result ? '閉じる' : 'キャンセル' }}
            </button>
            <button type="submit" :disabled="uploading || !form.file" class="btn-primary">
              {{ uploading ? 'アップロード中...' : 'インポート' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { poiApi } from '../../api/dataset.js'

const props = defineProps({
  datasetId: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['close', 'imported'])

const form = ref({
  format: '',
  file: null,
})

const uploading = ref(false)
const uploadProgress = ref(0)
const result = ref(null)
const error = ref(null)

function handleFileChange(event) {
  const file = event.target.files[0]
  if (file) {
    // ファイルサイズチェック（10MB）
    if (file.size > 10 * 1024 * 1024) {
      error.value = 'ファイルサイズが10MBを超えています'
      event.target.value = ''
      return
    }

    form.value.file = file
    error.value = null
  }
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
}

async function handleSubmit() {
  if (!form.value.file) {
    error.value = 'ファイルを選択してください'
    return
  }

  uploading.value = true
  uploadProgress.value = 0
  error.value = null
  result.value = null

  try {
    // 進捗シミュレーション（実際のAPIでは進捗イベントを使用）
    const progressInterval = setInterval(() => {
      if (uploadProgress.value < 90) {
        uploadProgress.value += 10
      }
    }, 200)

    const response = await poiApi.bulkImport(
      props.datasetId,
      form.value.file,
      form.value.format
    )

    clearInterval(progressInterval)
    uploadProgress.value = 100

    result.value = response.data
    setTimeout(() => {
      emit('imported', response.data)
    }, 1000)
  } catch (err) {
    error.value = err.response?.data?.error?.message || 'POIのインポートに失敗しました'
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 2rem;
  height: 2rem;
  line-height: 1;
}

.btn-close:hover {
  color: #212529;
}

.modal-body {
  padding: 1.5rem;
}

.info-box {
  background-color: #e7f3ff;
  border-left: 4px solid #007bff;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.info-box p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.form-group select,
.form-group input[type="file"] {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group small {
  display: block;
  margin-top: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.sample-format {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.sample-format h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  color: #495057;
}

.sample-format pre {
  background-color: #2d2d2d;
  color: #f8f8f2;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
}

.progress-bar {
  position: relative;
  height: 30px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.progress-fill {
  height: 100%;
  background-color: #007bff;
  transition: width 0.3s ease;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #212529;
  font-weight: 600;
}

.success-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  color: #155724;
}

.success-message p {
  margin: 0.25rem 0;
}

.error-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}
</style>
